import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card } from '../../components/ui/card';
import { toast } from 'sonner';
import { Users, User, Lock, Mail, Building2, Briefcase, ArrowRight, ArrowLeft, KeyRound, CheckCircle } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const AuthPage = () => {
  const [searchParams] = useSearchParams();
  const [loginTab, setLoginTab] = useState('family'); // 'family', 'professional', or 'institutional' (member hidden for now)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [familyLoginData, setFamilyLoginData] = useState({
    email: '',
    password: ''
  });
  const [professionalLoginData, setProfessionalLoginData] = useState({
    email: '',
    password: ''
  });
  const [institutionalLoginData, setInstitutionalLoginData] = useState({
    email: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  
  // Forgot Password States
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordStep, setForgotPasswordStep] = useState('request'); // 'request', 'reset', 'success'
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Check for tab query param
  useEffect(() => {
    const tab = searchParams.get('tab');
    if (tab === 'family' || tab === 'family-login') {
      setLoginTab('family');
    } else if (tab === 'professional' || tab === 'professional-login') {
      // Professional login temporarily hidden — fall back to Family
      setLoginTab('family');
    } else if (tab === 'institutional') {
      setLoginTab('institutional');
    }
    
    // Check for reset token in URL
    const token = searchParams.get('resetToken');
    if (token) {
      setResetToken(token);
      setShowForgotPassword(true);
      setForgotPasswordStep('reset');
    }
  }, [searchParams]);

  // Forgot Password Handler
  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          userType: loginTab
        })
      });
      
      const data = await response.json();
      if (data.success) {
        toast.success('Password reset instructions sent!');
        // For testing: auto-fill the reset token
        if (data.resetToken) {
          setResetToken(data.resetToken);
          setForgotPasswordStep('reset');
        }
      } else {
        toast.error(data.detail || 'Failed to send reset instructions');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Reset Password Handler
  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token: resetToken,
          newPassword: newPassword
        })
      });
      
      const data = await response.json();
      if (response.ok && data.success) {
        setForgotPasswordStep('success');
        toast.success('Password reset successful!');
      } else {
        toast.error(data.detail || 'Failed to reset password');
      }
    } catch (error) {
      toast.error('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Back to login
  const backToLogin = () => {
    setShowForgotPassword(false);
    setForgotPasswordStep('request');
    setForgotPasswordEmail('');
    setResetToken('');
    setNewPassword('');
    setConfirmPassword('');
  };

  // Member Login Handler
  const handleMemberLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const user = await login(formData.email, formData.password);
      toast.success('Login successful!');
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        // For non-admin members, redirect to members directory page
        navigate('/members');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  // Family Login Handler
  const handleFamilyLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/family-login`, familyLoginData);
      localStorage.setItem('familyToken', data.token);
      localStorage.setItem('familyId', data.familyId);
      localStorage.setItem('familyHeadName', data.headName);
      localStorage.setItem('familyEmail', data.email);
      toast.success('Family login successful!');
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/families-register');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Professional Login Handler
  const handleProfessionalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/professionals/login`, professionalLoginData);
      localStorage.setItem('professionalToken', data.token);
      localStorage.setItem('professionalId', data.professionalId);
      localStorage.setItem('professionalName', data.fullName);
      toast.success('Professional login successful!');
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/professional/directory');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleInstitutionalLogin = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const { data } = await axios.post(`${API_URL}/api/auth/institutional-login`, institutionalLoginData);
      localStorage.setItem('institutionalToken', data.token);
      localStorage.setItem('institutionalId', data.institution.id);
      localStorage.setItem('institutionalName', data.institution.sansthaName);
      toast.success('संस्थागत लॉगिन सफल! / Institutional login successful!');
      const next = searchParams.get('next');
      navigate(next && next.startsWith('/') && !next.startsWith('//') ? next : '/institutional/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-orange-50 to-white py-12 px-4">
      <div className="max-w-5xl mx-auto">
        
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold text-primary mb-2">
            सदस्यता एवं लॉगिन
          </h1>
          <p className="text-gray-600 text-lg">Membership & Login</p>
        </div>

        {/* Membership Types Section */}
        <div className="mb-12">
          <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
            सदस्यता के प्रकार / Membership Types
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Individual Membership - Coming Soon */}
            <Card className="p-6 bg-gray-50 shadow-lg border-t-4 border-gray-300 opacity-75">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gray-200 rounded-full flex items-center justify-center">
                  <User className="w-6 h-6 text-gray-400" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-600">व्यक्तिगत सदस्यता</h3>
                  <p className="text-sm text-gray-400">Individual Membership</p>
                </div>
              </div>
              
              <div className="space-y-3 text-gray-500 mb-6">
                <p>
                  ब्रह्मक्षत्रिय मारवाड़ी खत्री समाज का कोई भी व्यक्ति जो 18 वर्ष से अधिक आयु का है, 
                  व्यक्तिगत सदस्यता के लिये आवेदन कर सकता है।
                </p>
                <p className="text-sm">
                  व्यक्तिगत सदस्य महासभा की सभी गतिविधियों में भाग ले सकते हैं, 
                  मतदान का अधिकार प्राप्त कर सकते हैं तथा समाज के विकास में सक्रिय योगदान दे सकते हैं।
                </p>
              </div>
              
              <Button 
                className="w-full bg-gray-300 text-gray-500 cursor-not-allowed"
                disabled
              >
                व्यक्तिगत सदस्यता हेतु आवेदन करें
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <p className="text-center text-sm text-gray-500 mt-2 italic">
                (Coming Soon / जल्द आ रहा है)
              </p>
            </Card>

            {/* Institutional Membership */}
            <Card className="p-6 bg-white shadow-lg border-t-4 border-blue-600 hover:shadow-xl transition-shadow">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900">संस्थागत सदस्यता</h3>
                  <p className="text-sm text-gray-500">Institutional Membership</p>
                </div>
              </div>
              
              <div className="space-y-3 text-gray-700 mb-6">
                <p>
                  समाज की पंचायतें, संस्थाएं, ट्रस्ट एवं अन्य संगठन संस्थागत सदस्यता के लिये आवेदन कर सकते हैं।
                </p>
                <p className="text-sm">
                  संस्थागत सदस्यता से आपकी संस्था महासभा से जुड़कर समाज के व्यापक नेटवर्क का हिस्सा बनेगी 
                  तथा सामूहिक विकास कार्यों में सहभागिता कर सकेगी।
                </p>
              </div>
              
              <Link to="/institutional-membership">
                <Button className="w-full bg-blue-600 hover:bg-blue-700">
                  संस्थागत सदस्यता हेतु आवेदन करें
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </Card>
          </div>
        </div>

        {/* Divider */}
        <div className="relative my-12">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center">
            <span className="bg-gradient-to-b from-orange-50 to-white px-4 text-gray-500 text-sm">
              पहले से पंजीकृत हैं? / Already Registered?
            </span>
          </div>
        </div>

        {/* Login Section */}
        <div className="max-w-lg mx-auto">
          <Card className="p-6 bg-white shadow-lg">
            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
              लॉगिन करें / Login
            </h2>
            <p className="text-gray-500 text-sm text-center mb-6">
              अपने खाते में लॉगिन करें
            </p>

            {/* Login Type Tabs */}
            <div className="flex mb-6 border-b border-gray-200 overflow-x-auto">
              {/* Member tab hidden - individual membership deferred */}
              <button
                type="button"
                onClick={() => setLoginTab('family')}
                className={`flex-1 pb-3 text-center font-medium transition-colors whitespace-nowrap px-2 ${
                  loginTab === 'family'
                    ? 'text-green-600 border-b-2 border-green-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="family-login-tab"
              >
                <Users className="w-4 h-4 inline-block mr-1" />
                Family
              </button>
              {/* Professional login tab temporarily hidden */}
              <button
                type="button"
                onClick={() => setLoginTab('institutional')}
                className={`flex-1 pb-3 text-center font-medium transition-colors whitespace-nowrap px-2 ${
                  loginTab === 'institutional'
                    ? 'text-purple-600 border-b-2 border-purple-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
                data-testid="institutional-login-tab"
              >
                <Building2 className="w-4 h-4 inline-block mr-1" />
                Institutional
              </button>
            </div>

            {/* Member Login Form - Hidden for now */}
            {loginTab === 'member' && (
              <form onSubmit={handleMemberLogin} className="space-y-4">
                <div>
                  <Label htmlFor="memberEmail">
                    <Mail className="w-4 h-4 inline-block mr-1" />
                    Email
                  </Label>
                  <Input
                    id="memberEmail"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="your@email.com"
                    data-testid="auth-email-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="memberPassword">
                      <Lock className="w-4 h-4 inline-block mr-1" />
                      Password
                    </Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotPasswordEmail(formData.email);
                      }}
                      className="text-xs text-primary hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <Input
                    id="memberPassword"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    placeholder="Enter your password"
                    data-testid="auth-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary/90"
                  disabled={loading}
                  data-testid="auth-submit-button"
                >
                  {loading ? 'Please wait...' : 'Member Login / सदस्य लॉगिन'}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  सदस्य नहीं हैं?{' '}
                  <Link to="/member-registration" className="text-primary hover:underline font-medium">
                    पंजीकरण करें
                  </Link>
                </p>
              </form>
            )}

            {/* Family Login Form */}
            {loginTab === 'family' && (
              <form onSubmit={handleFamilyLogin} className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-green-700">
                    परिवार पंजीकरण के समय बनाए गए ईमेल और पासवर्ड से लॉगिन करें।
                  </p>
                </div>
                <div>
                  <Label htmlFor="familyEmail">
                    <Mail className="w-4 h-4 inline-block mr-1" />
                    Email
                  </Label>
                  <Input
                    id="familyEmail"
                    type="email"
                    value={familyLoginData.email}
                    onChange={(e) => setFamilyLoginData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="your@email.com"
                    data-testid="family-auth-email-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="familyPassword">
                      <Lock className="w-4 h-4 inline-block mr-1" />
                      Password
                    </Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotPasswordEmail(familyLoginData.email);
                      }}
                      className="text-xs text-green-600 hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <Input
                    id="familyPassword"
                    type="password"
                    value={familyLoginData.password}
                    onChange={(e) => setFamilyLoginData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    placeholder="Enter your password"
                    data-testid="family-auth-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-green-600 hover:bg-green-700"
                  disabled={loading}
                  data-testid="family-auth-submit-button"
                >
                  {loading ? 'Please wait...' : 'Family Login / परिवार लॉगिन'}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  परिवार पंजीकृत नहीं है?{' '}
                  <Link to="/family-registration" className="text-green-600 hover:underline font-medium">
                    परिवार पंजीकरण करें
                  </Link>
                </p>
              </form>
            )}

            {/* Professional Login Form */}
            {loginTab === 'professional' && (
              <form onSubmit={handleProfessionalLogin} className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                  <p className="text-sm text-blue-700">
                    प्रोफेशनल पंजीकरण के समय बनाए गए ईमेल और पासवर्ड से लॉगिन करें।
                  </p>
                </div>
                <div>
                  <Label htmlFor="professionalEmail">
                    <Mail className="w-4 h-4 inline-block mr-1" />
                    Email
                  </Label>
                  <Input
                    id="professionalEmail"
                    type="email"
                    value={professionalLoginData.email}
                    onChange={(e) => setProfessionalLoginData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="your@email.com"
                    data-testid="professional-auth-email-input"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <Label htmlFor="professionalPassword">
                      <Lock className="w-4 h-4 inline-block mr-1" />
                      Password
                    </Label>
                    <button
                      type="button"
                      onClick={() => {
                        setShowForgotPassword(true);
                        setForgotPasswordEmail(professionalLoginData.email);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      Forgot Password?
                    </button>
                  </div>
                  <Input
                    id="professionalPassword"
                    type="password"
                    value={professionalLoginData.password}
                    onChange={(e) => setProfessionalLoginData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    placeholder="Enter your password"
                    data-testid="professional-auth-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="professional-auth-submit-button"
                >
                  {loading ? 'Please wait...' : 'Professional Login / प्रोफेशनल लॉगिन'}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  प्रोफेशनल पंजीकृत नहीं हैं?{' '}
                  <Link to="/professional/register" className="text-blue-600 hover:underline font-medium">
                    प्रोफेशनल पंजीकरण करें
                  </Link>
                </p>
              </form>
            )}

            {/* Institutional Login Form */}
            {loginTab === 'institutional' && (
              <form onSubmit={handleInstitutionalLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="institutionalEmail">
                    <Mail className="w-4 h-4 inline-block mr-1" />
                    ईमेल / Email
                  </Label>
                  <Input
                    id="institutionalEmail"
                    type="email"
                    value={institutionalLoginData.email}
                    onChange={(e) => setInstitutionalLoginData(prev => ({ ...prev, email: e.target.value }))}
                    required
                    placeholder="Enter your registered email"
                    data-testid="institutional-auth-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="institutionalPassword">
                    <Lock className="w-4 h-4 inline-block mr-1" />
                    पासवर्ड / Password
                  </Label>
                  <Input
                    id="institutionalPassword"
                    type="password"
                    value={institutionalLoginData.password}
                    onChange={(e) => setInstitutionalLoginData(prev => ({ ...prev, password: e.target.value }))}
                    required
                    placeholder="Enter your password"
                    data-testid="institutional-auth-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  disabled={loading}
                  data-testid="institutional-auth-submit-button"
                >
                  {loading ? 'Please wait...' : 'Institutional Login / संस्थागत लॉगिन'}
                </Button>
                <p className="text-center text-sm text-gray-500">
                  संस्था पंजीकृत नहीं है?{' '}
                  <Link to="/institutional-membership" className="text-purple-600 hover:underline font-medium">
                    संस्थागत पंजीकरण करें
                  </Link>
                </p>
                <p className="text-center text-xs text-amber-600 bg-amber-50 p-2 rounded">
                  नोट: लॉगिन के बाद आप अपनी संस्था की जानकारी देख और संपादित कर सकते हैं।<br/>
                  Note: After login, you can view and edit your institution details.
                </p>
              </form>
            )}
          </Card>

          {/* Forgot Password Modal */}
          {showForgotPassword && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
              <Card className="w-full max-w-md p-6 bg-white rounded-xl shadow-2xl">
                {/* Request Password Reset */}
                {forgotPasswordStep === 'request' && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                        <KeyRound className="w-6 h-6 text-primary" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Forgot Password?</h2>
                        <p className="text-sm text-gray-500">पासवर्ड भूल गए?</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4 text-sm">
                      Enter your registered email address and we'll help you reset your password.
                    </p>
                    
                    <form onSubmit={handleForgotPassword} className="space-y-4">
                      <div>
                        <Label htmlFor="forgotEmail">
                          <Mail className="w-4 h-4 inline-block mr-1" />
                          Email Address
                        </Label>
                        <Input
                          id="forgotEmail"
                          type="email"
                          value={forgotPasswordEmail}
                          onChange={(e) => setForgotPasswordEmail(e.target.value)}
                          required
                          placeholder="your@email.com"
                        />
                      </div>
                      
                      <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                        <span className="text-sm text-gray-600">Account Type:</span>
                        <span className={`text-sm font-medium ${
                          loginTab === 'member' ? 'text-primary' : 
                          loginTab === 'family' ? 'text-green-600' : 'text-blue-600'
                        }`}>
                          {loginTab === 'member' ? 'Member / सदस्य' : 
                           loginTab === 'family' ? 'Family / परिवार' : 'Professional / प्रोफेशनल'}
                        </span>
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={backToLogin}
                          className="flex-1"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Back
                        </Button>
                        <Button
                          type="submit"
                          className="flex-1 bg-primary hover:bg-primary/90"
                          disabled={loading}
                        >
                          {loading ? 'Sending...' : 'Send Reset Link'}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Reset Password Form */}
                {forgotPasswordStep === 'reset' && (
                  <div>
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                        <Lock className="w-6 h-6 text-green-600" />
                      </div>
                      <div>
                        <h2 className="text-xl font-bold text-gray-900">Reset Password</h2>
                        <p className="text-sm text-gray-500">नया पासवर्ड सेट करें</p>
                      </div>
                    </div>
                    
                    <p className="text-gray-600 mb-4 text-sm">
                      Enter your new password below. Make sure it's at least 6 characters.
                    </p>
                    
                    <form onSubmit={handleResetPassword} className="space-y-4">
                      <div>
                        <Label htmlFor="newPassword">New Password / नया पासवर्ड</Label>
                        <Input
                          id="newPassword"
                          type="password"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          required
                          placeholder="Enter new password"
                          minLength={6}
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="confirmPassword">Confirm Password / पासवर्ड पुष्टि करें</Label>
                        <Input
                          id="confirmPassword"
                          type="password"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          placeholder="Confirm new password"
                          minLength={6}
                        />
                      </div>
                      
                      <div className="flex gap-3">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={backToLogin}
                          className="flex-1"
                        >
                          <ArrowLeft className="w-4 h-4 mr-2" />
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          disabled={loading}
                        >
                          {loading ? 'Resetting...' : 'Reset Password'}
                        </Button>
                      </div>
                    </form>
                  </div>
                )}

                {/* Success Message */}
                {forgotPasswordStep === 'success' && (
                  <div className="text-center">
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                      <CheckCircle className="w-8 h-8 text-green-600" />
                    </div>
                    <h2 className="text-xl font-bold text-gray-900 mb-2">Password Reset Successful!</h2>
                    <p className="text-gray-600 mb-6">
                      पासवर्ड सफलतापूर्वक रीसेट हो गया! अब आप नए पासवर्ड से लॉगिन कर सकते हैं।
                    </p>
                    <Button
                      onClick={backToLogin}
                      className="w-full bg-primary hover:bg-primary/90"
                    >
                      Back to Login / लॉगिन पर वापस जाएं
                    </Button>
                  </div>
                )}
              </Card>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default AuthPage;
